import { useState, useEffect } from 'react';
import { Building2, CreditCard, AlertCircle, X, Check, ChevronRight } from 'lucide-react';
import apiClient from '../../api/client';

/**
 * BankChannelModal
 * Shown after a sale with Bank Transfer/POS/Card payment method
 * Allows staff to select which bank account the payment was received into
 */
const BankChannelModal = ({ 
  isOpen, 
  onClose, 
  onSelect, 
  onSkip, 
  saleAmount = 0,
  paymentMethod = 'transfer' 
}) => {
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [estimatedCharge, setEstimatedCharge] = useState(null);

  useEffect(() => {
    if (isOpen) {
      fetchChannels();
    }
  }, [isOpen]);

  useEffect(() => {
    if (selectedChannel && saleAmount > 0) {
      calculateCharge(selectedChannel.id, saleAmount);
    } else {
      setEstimatedCharge(null);
    }
  }, [selectedChannel, saleAmount]);

  const fetchChannels = async () => {
    try {
      setLoading(true);
      // Get channels that match the payment method type
      let type = '';
      if (paymentMethod === 'transfer' || paymentMethod === 'bank_transfer') {
        type = 'bank_transfer';
      } else if (paymentMethod === 'pos') {
        type = 'pos';
      } else if (paymentMethod === 'card') {
        type = 'card';
      }
      
      const params = type ? `?type=${type}` : '';
      const response = await apiClient.get(`/bank-channels/active${params}`);
      setChannels(response.data.data || []);
    } catch (error) {
      console.error('Error fetching bank channels:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateCharge = async (channelId, amount) => {
    try {
      const response = await apiClient.get(
        `/bank-channels/calculate-charge?channel_id=${channelId}&amount=${amount}`
      );
      setEstimatedCharge(response.data.data);
    } catch (error) {
      console.error('Error calculating charge:', error);
    }
  };

  const handleConfirm = () => {
    if (selectedChannel) {
      onSelect({
        bank_channel_id: selectedChannel.id,
        channel_name: selectedChannel.channel_name,
        estimated_charge: estimatedCharge?.estimated_charge || 0
      });
    }
  };

  const getChannelIcon = (type) => {
    switch (type) {
      case 'bank_transfer': return <Building2 className="h-5 w-5" />;
      case 'pos': return <CreditCard className="h-5 w-5" />;
      case 'card': return <CreditCard className="h-5 w-5" />;
      default: return <Building2 className="h-5 w-5" />;
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b bg-gradient-to-r from-amber-500 to-amber-600 text-white rounded-t-lg">
          <div>
            <h2 className="text-lg font-semibold">Select Bank Channel</h2>
            <p className="text-sm text-amber-100">
              Which account was this {formatCurrency(saleAmount)} paid into?
            </p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-amber-400 rounded">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4">
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500"></div>
            </div>
          ) : channels.length === 0 ? (
            <div className="text-center py-8">
              <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-600 mb-2">No bank channels configured</p>
              <p className="text-sm text-gray-400">
                Ask admin to add bank accounts in Settings
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {channels.map((channel) => (
                <button
                  key={channel.id}
                  onClick={() => setSelectedChannel(channel)}
                  className={`w-full flex items-center gap-3 p-3 rounded-lg border-2 transition-all ${
                    selectedChannel?.id === channel.id
                      ? 'border-amber-500 bg-amber-50'
                      : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <div className={`p-2 rounded-lg ${
                    channel.channel_type === 'bank_transfer' ? 'bg-blue-100 text-blue-600' :
                    channel.channel_type === 'pos' ? 'bg-green-100 text-green-600' :
                    'bg-purple-100 text-purple-600'
                  }`}>
                    {getChannelIcon(channel.channel_type)}
                  </div>
                  <div className="flex-1 text-left">
                    <div className="font-medium text-gray-900">{channel.channel_name}</div>
                    <div className="text-sm text-gray-500">
                      {channel.bank_name}
                      {(parseFloat(channel.base_charge) > 0 || parseFloat(channel.charge_percentage) > 0) && (
                        <span className="ml-2 text-amber-600">
                          • Charge: {parseFloat(channel.base_charge) > 0 ? `₦${channel.base_charge}` : ''} 
                          {parseFloat(channel.base_charge) > 0 && parseFloat(channel.charge_percentage) > 0 ? ' + ' : ''}
                          {parseFloat(channel.charge_percentage) > 0 ? `${channel.charge_percentage}%` : ''}
                        </span>
                      )}
                    </div>
                  </div>
                  {selectedChannel?.id === channel.id && (
                    <Check className="h-5 w-5 text-amber-500" />
                  )}
                </button>
              ))}
            </div>
          )}

          {/* Estimated Charge Display */}
          {selectedChannel && estimatedCharge && (
            <div className="mt-4 p-3 bg-gray-50 rounded-lg">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Sale Amount:</span>
                <span className="font-medium">{formatCurrency(saleAmount)}</span>
              </div>
              <div className="flex justify-between text-sm text-red-600 mt-1">
                <span>Estimated Charge:</span>
                <span>-{formatCurrency(estimatedCharge.estimated_charge)}</span>
              </div>
              {parseFloat(estimatedCharge.min_charge) !== parseFloat(estimatedCharge.max_charge) && (
                <div className="text-xs text-gray-400 mt-1">
                  (Range: {formatCurrency(estimatedCharge.min_charge)} to {formatCurrency(estimatedCharge.max_charge)})
                </div>
              )}
              <div className="flex justify-between text-sm font-semibold mt-2 pt-2 border-t">
                <span className="text-gray-700">Net Amount:</span>
                <span className="text-green-600">{formatCurrency(estimatedCharge.net_amount)}</span>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t bg-gray-50 rounded-b-lg">
          <div className="flex gap-3">
            <button
              onClick={onSkip}
              className="flex-1 px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Select Later
            </button>
            <button
              onClick={handleConfirm}
              disabled={!selectedChannel}
              className={`flex-1 px-4 py-2 rounded-lg flex items-center justify-center gap-2 transition-colors ${
                selectedChannel
                  ? 'bg-amber-500 hover:bg-amber-600 text-white'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }`}
            >
              Confirm
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
          <p className="text-xs text-center text-gray-400 mt-3">
            Admin will verify the payment after confirmation
          </p>
        </div>
      </div>
    </div>
  );
};

export default BankChannelModal;
